<script lang="ts" setup>
import { ImageComponent, SocialMediaAccounts } from '@metapic-apps/shared'
import { ElIcon, ElPopover } from 'element-plus'
import { computed, ref } from 'vue'

import ArrowIcon from '@/assets/icons/arrow.svg?component'
import MetapicIcon from '@/assets/icons/metapic-icon-black.svg?component'
import StatsIcon from '@/assets/icons/stats.svg?component'
import UserCardAvatarIcon from '@/assets/icons/user-default.svg?component'
import { STATUS_TITLE_MAP } from '@/components/CampaignParticipants/CampaignParticipantsColumns.helpers'
import { normalizeImage } from '@/lib/adapters'
import { normalizeSocialItem } from '@/lib/adapters/creators.adapter'
import { type CampaignParticipantDetailsType, type ParticipantStatus } from '@/types/campaignParticipant.types'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const props = defineProps<{
  participant: CampaignParticipantDetailsType
  columnIds: ParticipantStatus[]
  hideStatisticsBtn: boolean
}>()
const emits = defineEmits(['move'])

const popoverRef = ref()
const isAvatarErrorLoad = ref(false)
const currentColumnFormValue = ref<ParticipantStatus>(props.participant.status)

const socialMediaAccounts = computed(() => props.participant.social_media?.map(normalizeSocialItem) ?? [])

const errorAvatarHandler = () => (isAvatarErrorLoad.value = true)
const moveParticipant = () => {
  popoverRef.value.hide()
  if (props.participant.status !== currentColumnFormValue.value) {
    emits('move', currentColumnFormValue.value)
  }
}

const participantAvatar = computed(() => (props.participant.creator_avatar ? normalizeImage(props.participant.creator_avatar) : null))
</script>
<template>
  <div class="participant-details-header__container">
    <BoxComponent :mb="24">
      <FlexComponent :gap="16" align="center">
        <ImageComponent
          v-if="participantAvatar && !isAvatarErrorLoad"
          :image="participantAvatar"
          :width="80"
          :height="80"
          alt="avatar"
          class="participant-details-header__avatar"
          @on-load-error="errorAvatarHandler"
        />
        <ElIcon v-else :size="19" class="participant-details-header__default-avatar">
          <UserCardAvatarIcon />
        </ElIcon>
        <FlexComponent :gap="8" direction="column" justify="center" class="participant-details-header__title">
          <FlexComponent v-if="participant.display_name" :gap="8" align="center">
            <TypographyComponent variant="headline-medium" text-truncate>
              {{ participant.display_name }}
            </TypographyComponent>
            <ElPopover
              v-if="participant.id"
              :width="240"
              trigger="hover"
              :teleported="false"
              :show-arrow="false"
              popper-class="participant-details-content__icon-popover"
            >
              <BoxComponent :pl="16" :pr="16">
                <TypographyComponent variant="title-medium">Metapic creator</TypographyComponent>
                <TypographyComponent variant="body-medium">This creator has a verified Metapic account.</TypographyComponent>
              </BoxComponent>
              <template #reference>
                <ElIcon :size="16">
                  <MetapicIcon />
                </ElIcon>
              </template>
            </ElPopover>
          </FlexComponent>
          <SocialMediaAccounts :social-media="socialMediaAccounts" justify="flex-start" />
          <TypographyComponent variant="body-medium">
            in list
            <span class="participant-details-header__column-name">{{ STATUS_TITLE_MAP[participant.status] }}</span>
          </TypographyComponent>
        </FlexComponent>
      </FlexComponent>
    </BoxComponent>
    <FlexComponent :gap="8">
      <ElPopover
        ref="popoverRef"
        :teleported="false"
        :show-arrow="false"
        :width="280"
        placement="bottom-start"
        popper-class="participant-details-content__popover"
        trigger="click"
      >
        <BoxComponent :pl="16" :pr="16">
          <FlexComponent :gap="16" direction="column">
            <TypographyComponent variant="headline-small"> Move </TypographyComponent>
            <BoxComponent>
              <TypographyComponent variant="title-large">Select target list</TypographyComponent>
              <el-select v-model="currentColumnFormValue" :teleported="false" size="large">
                <el-option v-for="item in columnIds" :key="item" :value="item" :label="STATUS_TITLE_MAP[item]" />
              </el-select>
            </BoxComponent>
            <div>
              <ButtonComponent variant="accent" @click="moveParticipant">Move</ButtonComponent>
            </div>
          </FlexComponent>
        </BoxComponent>
        <template #reference>
          <el-button type="info" round>
            <FlexComponent :gap="8" align="center">
              <ElIcon :size="18">
                <ArrowIcon />
              </ElIcon>
              <TypographyComponent variant="label-large"> Move </TypographyComponent>
            </FlexComponent>
          </el-button>
        </template>
      </ElPopover>
      <router-link
        v-if="participant.user_id && !hideStatisticsBtn"
        :to="{ name: 'creator', params: { id: participant.user_id } }"
        target="_blank"
      >
        <el-button type="info" round>
          <FlexComponent :gap="8" align="center">
            <ElIcon :size="18">
              <StatsIcon />
            </ElIcon>
            <TypographyComponent variant="label-large"> Statistics </TypographyComponent>
          </FlexComponent>
        </el-button>
      </router-link>
    </FlexComponent>
  </div>
</template>
<style lang="scss" scoped>
a:hover {
  text-decoration: none;
}
.participant-details-header__container {
  margin: -24px -24px -24px -24px;
  padding: 24px;
  background: #f3f7f9;
}
.participant-details-header__avatar {
  border-radius: 80px;
}
.participant-details-header__default-avatar {
  background-color: #fbe9f2;
  border-radius: 50%;
  flex-shrink: 0;
  width: 80px;
  height: 80px;
}
.participant-details-header__title {
  overflow: hidden;
  padding-right: 32px;
}
</style>
